import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Link, useParams} from 'react-router-dom'
import {QRCodeCanvas} from 'qrcode.react'

const API_URL = process.env.REACT_APP_API_URL
const GET_GRADUATE_CARD_URL = `${API_URL}/Custom/GetGraduateCard`

type GraduateCardData = {
  fullName?: string
  studentNumber?: string
  organizationName?: string
  graduateDate?: string
  gpa?: number
  email?: string
  phone?: string
  isWorking?: boolean
  employerName?: string
  isJobSeeking?: boolean
  cardCode?: string
  approvedAt?: string
  photoUrl?: string
  departmentName?: string
  facultyName?: string
  documentValidUntil?: string
  institutionName?: string
  institutionLogoUrl?: string
}

const GraduateCardPage: React.FC = () => {
  const {code} = useParams<{code: string}>()
  const [data, setData] = useState<GraduateCardData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!code) {
      setError('Geçersiz bağlantı.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    axios
      .get(`${GET_GRADUATE_CARD_URL}?code=${code}`)
      .then((res) => {
        setData(res.data as GraduateCardData)
      })
      .catch(() => {
        setError('Mezun kartı bulunamadı veya aktif değil.')
      })
      .finally(() => setLoading(false))
  }, [code])

  const currentUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.location.href
    }
    if (code) {
      return `/gc/${code}`
    }
    return ''
  }, [code])

  const shareUrl = useMemo(() => {
    const cardCode = data?.cardCode
    if (cardCode) {
      if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}/gc/${cardCode}`
      }
      return `/gc/${cardCode}`
    }
    return currentUrl
  }, [currentUrl, data?.cardCode])

  const verificationUrl = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/gc`
    }
    return '/gc'
  }, [])

  if (loading) {
    return (
      <div className='container py-10'>
        <div className='alert alert-info text-center'>Mezun kartı yükleniyor...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='container py-10'>
        <div className='alert alert-danger text-center'>{error}</div>
      </div>
    )
  }

  if (!data) {
    return null
  }

  const watermarkUrl = '/media/patterns/vector-1.png'

  const formatDate = (value?: string | null) => {
    if (!value) {
      return '-'
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return '-'
    }

    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

    const buildAbsoluteUrl = (path?: string | null) => {
        if (!path) return undefined;

        // data: veya tam http(s) URL'leri olduğu gibi döndür
        if (path.startsWith('data:')) return path;
        if (/^https?:\/\//i.test(path)) return path;

        if (!API_URL) return path;

        // Sondaki /'ları temizle
        const trimmed = API_URL.replace(/\/+$/, '');

        // Son 4 karakteri güvenli şekilde at (4'ten kısaysa tamamını boşaltır)
        const baseMinus4 = trimmed.slice(0, Math.max(0, trimmed.length - 4));

        // path'i / ile normalize et
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;

        return `${baseMinus4}${normalizedPath}`;
    };


  const institutionTitle = data.institutionName ?? data.organizationName ?? ''
  const institutionLogoUrl = buildAbsoluteUrl(data.institutionLogoUrl)
  const departmentName = data.departmentName ?? data.organizationName
  const facultyName = data.facultyName

  const infoRows = [
    {label: 'Ad Soyad', value: data.fullName ?? '-'},
    {label: 'Fakülte', value: facultyName ?? '-'},
    {label: 'Bölüm', value: departmentName ?? '-'},
    {label: 'Öğrenci Numarası', value: data.studentNumber ?? '-'},
    {label: 'Mezuniyet Tarihi', value: formatDate(data.graduateDate)},
    {label: 'Belge Geçerlilik Tarihi', value: formatDate(data.documentValidUntil)},
  ]

  const initials = (data.fullName ?? '')
    .split(' ')
    .filter((part) => part.trim().length > 0)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()

  return (
    <div className='container py-10'>
      <div className='mx-auto' style={{maxWidth: '880px'}}>
        <div
          className='position-relative overflow-hidden rounded-4 shadow-lg'
          style={{backgroundColor: 'rgba(248, 250, 252, 0.9)'}}
        >
          <div
            className='position-absolute top-0 start-0 w-100 h-100'
            style={{
              backgroundImage: `url(${watermarkUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.12,
              pointerEvents: 'none',
            }}
          />
          <div className='position-relative'>
            <div className='row g-0 align-items-stretch'>
              <div className='col-12 col-md-4 border-end border-light'>
                <div
                  className='h-100 d-flex flex-column justify-content-between text-center text-md-start p-5'
                  style={{
                    background: 'linear-gradient(180deg, rgba(30,64,175,0.92) 0%, rgba(15,23,42,0.92) 100%)',
                    color: '#fff',
                  }}
                >
                  <div className='text-center'>
                    <div
                      className='mx-auto d-flex align-items-center justify-content-center shadow-lg'
                      style={{
                        width: '180px',
                        height: '220px',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(15, 23, 42, 0.35)',
                        border: '2px solid rgba(255,255,255,0.35)',
                      }}
                    >
                      {data.photoUrl ? (
                        <img
                          src={data.photoUrl}
                          alt='Öğrenci Fotoğrafı'
                          style={{width: '100%', height: '100%', objectFit: 'cover'}}
                        />
                      ) : (
                        <span className='fw-bold fs-1 text-uppercase'>{initials || '??'}</span>
                      )}
                    </div>
                    <div className='mt-4'>
                      <div className='text-uppercase small text-white-50'>Kart Tipi</div>
                      <div className='fw-semibold fs-4'>Mezun Kartı</div>
                    </div>
                  </div>
                  <div className='mt-5 text-center'>
                    {data.cardCode && (
                      <div className='d-flex flex-column gap-3'>
                        <div>
                          <div className='text-uppercase small text-white-50'>Doğrulama Kodu</div>
                          <div className='fw-semibold fs-6'>{data.cardCode}</div>
                        </div>
                        <div>
                          <div className='text-uppercase small text-white-50'>Doğrulama Adresi</div>
                          <div className='fw-semibold fs-6 text-white text-break'>{verificationUrl}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-8'>
                <div
                  className='h-100 d-flex flex-column justify-content-between p-5'
                  style={{backgroundColor: 'rgba(255,255,255,0.88)'}}
                >
                                  <div>

                                      <div className='d-flex flex-column align-items-center text-center gap-2 mb-5'>
                                          {institutionLogoUrl && (
                                              <img
                                                  src={institutionLogoUrl}
                                                  alt='Kurum Logosu'
                                                  className='d-block mx-auto'
                                                  style={{ maxHeight: '72px', maxWidth: '160px', objectFit: 'contain' }}
                                              />
                                          )}

                                          {institutionTitle && (
                                              <div className='text-uppercase text-muted fw-semibold small mt-2'>
                                                  {institutionTitle}
                                              </div>
                                          )}
                                      </div>

                    <div>
                      {infoRows.map((row) => (
                        <div
                          key={row.label}
                          className='d-flex flex-column flex-sm-row justify-content-between align-items-sm-center py-3 border-bottom'
                          style={{borderColor: 'rgba(15,23,42,0.08)'}}
                        >
                          <span className='text-uppercase text-muted small fw-semibold'>{row.label}</span>
                          <span className='fs-5 fw-semibold text-dark'>{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='mt-5'>
                    <div className='d-flex flex-column flex-md-row align-items-center justify-content-between gap-4'>
                      <div className='text-muted small text-center text-md-start'>
                        Doğrulama için QR kodunu tarayabilir veya bağlantıyı paylaşabilirsiniz.
                      </div>
                      <QRCodeCanvas value={shareUrl} size={160} includeMargin={true} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='text-center mt-6'>
        <Link to='/gc' className='btn btn-link px-0'>
          Farklı bir mezun kartı doğrula
        </Link>
      </div>
    </div>
  )
}

export default GraduateCardPage
